/**
 * Cluster Metrics Configuration API
 *
 * Manages per-cluster metrics tokens for VictoriaMetrics/InfluxDB integration.
 * Each cluster can have its own write token for vmauth authentication.
 */

import { apiClient } from './api'

// Types
export type ClusterMetricsStatus = 'pending' | 'configured' | 'error' | 'disabled'

export type ClusterMetricsConfig = {
  id: string
  cluster_id: string
  bucket_name: string
  metric_server_name: string
  status: ClusterMetricsStatus
  status_message: string | null
  is_enabled: boolean
  is_proxmox_configured: boolean
  proxmox_configured_at: string | null
  proxmox_config_method: string | null
  created_at: string
  updated_at: string
  created_by_id: string | null
  cluster_name?: string
}

export type ClusterMetricsConfigWithCluster = {
  cluster_name: string
  cluster_status: string
  organization_id: string
  organization_name: string | null
} & ClusterMetricsConfig

export type ClusterMetricsConfigCreate = {
  write_token: string
  bucket_name?: string
  metric_server_name?: string
  is_enabled?: boolean
}

export type ClusterMetricsConfigUpdate = {
  write_token?: string
  bucket_name?: string
  metric_server_name?: string
  is_enabled?: boolean
}

export type ApplyMetricsConfigRequest = {
  force?: boolean
}

export type ApplyMetricsConfigResponse = {
  success: boolean
  message: string
  method: string
  bucket_name?: string
  metric_server_name?: string
  proxmox_output?: string
  error?: string
}

export type MetricsConfigStatusResponse = {
  has_org_config: boolean
  has_cluster_config: boolean
  is_proxmox_configured: boolean
  is_receiving_data: boolean
  org_config_url?: string
  org_config_server_type?: string
  cluster_bucket?: string
  cluster_status?: ClusterMetricsStatus
  last_data_received?: string
  data_points_last_hour?: number
  errors: string[]
  warnings: string[]
}

export type ProxmoxMetricServerConfig = {
  server_id?: string
  type: string
  server: string
  port: number
  protocol: string
  organization: string
  bucket: string
  timeout: number
  enabled: boolean
  cli_command?: string
  api_command?: string
}

// API Methods
export const clusterMetricsApi = {
  /**
   * Get the metrics configuration for a cluster
   */
  getConfig: (clusterId: string): Promise<ClusterMetricsConfig> =>
    apiClient.get(`/api/v1/clusters/${clusterId}/metrics-config`),

  /**
   * Create a metrics configuration for a cluster
   */
  createConfig: (
    clusterId: string,
    data: ClusterMetricsConfigCreate,
  ): Promise<ClusterMetricsConfig> =>
    apiClient.post(`/api/v1/clusters/${clusterId}/metrics-config`, data),

  /**
   * Update the metrics configuration for a cluster
   */
  updateConfig: (
    clusterId: string,
    data: ClusterMetricsConfigUpdate,
  ): Promise<ClusterMetricsConfig> =>
    apiClient.put(`/api/v1/clusters/${clusterId}/metrics-config`, data),

  /**
   * Delete the metrics configuration for a cluster
   */
  deleteConfig: (clusterId: string): Promise<void> =>
    apiClient.delete(`/api/v1/clusters/${clusterId}/metrics-config`),

  /**
   * Apply the metrics configuration to Proxmox
   */
  applyConfig: (
    clusterId: string,
    data?: ApplyMetricsConfigRequest,
  ): Promise<ApplyMetricsConfigResponse> =>
    apiClient.post(`/api/v1/clusters/${clusterId}/metrics-config/apply`, data || {}),

  /**
   * Get the complete status of metrics configuration for a cluster
   */
  getStatus: (clusterId: string): Promise<MetricsConfigStatusResponse> =>
    apiClient.get(`/api/v1/clusters/${clusterId}/metrics-config/status`),

  /**
   * Get the Proxmox metric server configuration for manual setup
   */
  getProxmoxConfig: (clusterId: string): Promise<ProxmoxMetricServerConfig> =>
    apiClient.get(`/api/v1/clusters/${clusterId}/metrics-config/proxmox-config`),

  /**
   * List all cluster metrics configurations (admin only)
   */
  listAll: (organizationId?: string): Promise<ClusterMetricsConfigWithCluster[]> => {
    const params = organizationId ? `?organization_id=${organizationId}` : ''
    return apiClient.get(`/api/v1/admin/cluster-metrics-configs${params}`)
  },
}

export default clusterMetricsApi
